/**
 * ServiceSlipPrintModal — A5 printable slip for HA Service tickets.
 *
 * Two modes:
 *   • mode="dropoff"    → Pre-Service Acknowledgement (handed to the patient
 *                          when the hearing aid is dropped off for service).
 *   • mode="completion" → Service Completion Report  (handed to the patient
 *                          at pick-up, listing work done + final amount).
 *
 * Uses the same `window.print()` + body-class scoping pattern as
 * HearingReportPreviewModal — see `body.printing-service-slip` in App.css.
 * Internal-reference images captured on the ticket are NOT rendered here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import useEscapeKey from '../../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const ACCESSORY_LABELS = {
  charger: 'Charger',
  dome: 'Dome',
  wax_guards: 'Wax-guards',
  ear_hook: 'Ear-hook',
  cable: 'Cable',
  case: 'Case',
  battery: 'Battery',
  cleaning_tool: 'Cleaning tool',
};

const SIDE_LABEL = { left: 'Left', right: 'Right', both: 'Both' };
// Canonical print order for the accessories row on the A5 slip — matches
// the order the checkboxes appear in the New-Ticket form so the printed
// list reads consistently regardless of the tick-order at intake. Custom
// always sits at the end.
const ACCESSORY_PRINT_ORDER = [
  'charger', 'dome', 'wax_guards', 'ear_hook', 'cable', 'case',
  'battery', 'cleaning_tool', 'custom',
];
const KIND_LABEL = {
  repair: 'Repair', cleaning: 'Cleaning', reprogramming: 'Reprogramming',
  warranty_claim: 'Warranty Claim', other: 'Other',
};

export default function ServiceSlipPrintModal({ ticket, pipeline, mode, onClose }) {
  useEscapeKey(onClose);
  const [clinic, setClinic] = useState(null);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/settings/clinic`, { params: ticket?.branch_id ? { branch_id: ticket.branch_id } : {} }).catch(() => ({ data: null })),
          ticket?.patient_id
            ? axios.get(`${API}/patients/${ticket.patient_id}`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        setClinic(c.data || null);
        setPatient(p.data || null);
      } catch {
        /* noop — best-effort fetch */
      }
    })();
  }, [ticket?.patient_id]);

  // Toggle body class + inject an A5 `@page` rule that wins the cascade
  // against App.css's top-of-file A4 `@page`. Removed on unmount so the
  // other print flows (Report, EOD, Invoice) keep their A4 paper size.
  useEffect(() => {
    document.body.classList.add('printing-service-slip');
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-testid', 'service-slip-page-css');
    styleEl.textContent = '@media print { @page { size: A5 portrait; margin: 8mm; } }';
    document.head.appendChild(styleEl);
    return () => {
      document.body.classList.remove('printing-service-slip');
      if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, []);

  const doPrint = () => window.print();

  // Approved-estimate lookup → the "final amount" printed on the completion
  // slip is what was CONVEYED to the patient at approval time (vendor cost
  // + clinic margin + GST, per billing.py which treats conveyed_amount as
  // GST-inclusive). We ONLY consider APPROVED estimates for the amount so
  // an un-approved (possibly higher) vendor quote never accidentally prints
  // as the "final charged" figure. The `latestEstimate` is used for the
  // Work-Performed narrative fallback only — never for money.
  const approvedEstimate = useMemo(() => {
    if (!pipeline?.estimates?.length) return null;
    const approvedIds = new Set(
      (pipeline.approvals || [])
        .filter(a => a.decision === 'APPROVED')
        .map(a => a.estimate_id),
    );
    const approvedList = pipeline.estimates.filter(e => approvedIds.has(e.estimate_id));
    return approvedList.length ? approvedList[approvedList.length - 1] : null;
  }, [pipeline]);

  const latestEstimate = useMemo(() => (
    pipeline?.estimates?.length
      ? pipeline.estimates[pipeline.estimates.length - 1]
      : null
  ), [pipeline]);

  const finalAmount = useMemo(() => {
    if (!approvedEstimate) return null;
    if (approvedEstimate.warranty_covered) return 0;
    const conveyed = Number(approvedEstimate.conveyed_amount ?? approvedEstimate.amount ?? 0);
    const disc = Number(approvedEstimate.discount ?? 0);
    return Math.max(0, conveyed - disc);
  }, [approvedEstimate]);

  const t = ticket || {};
  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
  const receivedDate = t.created_at
    ? new Date(t.created_at).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : today;

  const brandLine = () => {
    const parts = [t.instrument_make, t.instrument_model].filter(Boolean);
    return parts.length ? parts.join(' ') : (t.serial_no || '—');
  };

  const accessoriesText = () => {
    const items = (t.accessories_given || [])
      .slice()
      .sort((a, b) => ACCESSORY_PRINT_ORDER.indexOf(a) - ACCESSORY_PRINT_ORDER.indexOf(b))
      .map(k => k === 'custom' ? (t.custom_accessory || 'Other') : (ACCESSORY_LABELS[k] || k));
    return items.length ? items.join(', ') : 'None';
  };

  const warrantyCardText = () => {
    if (t.warranty_card_given === true) return 'Yes';
    if (t.warranty_card_given === false) return 'No';
    if (t.warranty_card_fs_id) return 'Yes (on file)';
    return '—';
  };

  const sideText = () => {
    if (t.hearing_aid_side) return SIDE_LABEL[t.hearing_aid_side];
    if (t.instrument_count === 'both') return 'Both';
    if (t.instrument_count === 'single') return 'Single';
    return '—';
  };

  const title = mode === 'dropoff'
    ? 'Pre-Service Acknowledgement'
    : 'Service Completion Report';

  const CELL_BASE = { border: '0.5px solid #cbd5e1', padding: '3px 5px' };
  const CELL_LBL = { ...CELL_BASE, fontWeight: 700, background: '#f8fafc' };
  const SECTION = {
    fontSize: 10, fontWeight: 800, marginTop: 6, marginBottom: 3,
    textTransform: 'uppercase', letterSpacing: '0.5px', color: '#0f172a',
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex flex-col"
         data-testid={`service-slip-modal-${mode}`}>
      {/* Toolbar (hidden on print) */}
      <div className="no-print bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between">
        <div className="text-sm font-bold text-slate-800">{title} · A5</div>
        <div className="flex gap-2">
          <button onClick={doPrint} data-testid={`service-slip-print-${mode}`}
                  className="px-3 py-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded">
            🖨️ Print
          </button>
          <button onClick={onClose} data-testid={`service-slip-close-${mode}`}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded">
            Close
          </button>
        </div>
      </div>

      {/* A5 canvas */}
      <div className="flex-1 overflow-auto bg-slate-200 p-6 flex justify-center items-start">
        <div id="service-slip-a5" className="bg-white shadow-xl" style={{
          width: '148mm', minHeight: '210mm', padding: '8mm',
          fontFamily: 'Arial, sans-serif', fontSize: '10px', lineHeight: 1.35,
          color: '#0f172a', boxSizing: 'border-box',
        }}>
          {/* Clinic header */}
          <div style={{ borderBottom: '1.5px solid #0f172a', paddingBottom: 4, marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.5px' }}>
              {clinic?.name || 'Audiology Clinic'}
            </div>
            {clinic?.tagline && (
              <div style={{ fontSize: 9, color: '#475569', fontStyle: 'italic' }}>{clinic.tagline}</div>
            )}
            <div style={{
              fontSize: 9, color: '#475569', marginTop: 2,
              display: 'flex', flexWrap: 'wrap', gap: 6,
            }}>
              {clinic?.address && <span>{clinic.address}</span>}
              {clinic?.phone && <span>· Ph: {clinic.phone}</span>}
              {clinic?.email && <span>· {clinic.email}</span>}
              {clinic?.gstin && <span>· GSTIN {clinic.gstin}</span>}
            </div>
          </div>

          {/* Title band */}
          <div style={{
            background: '#0f172a', color: 'white', padding: '4px 8px',
            margin: '4px 0 6px', fontSize: 12, fontWeight: 800,
            textAlign: 'center', letterSpacing: '1px', textTransform: 'uppercase',
          }} data-testid={`service-slip-title-${mode}`}>
            {title}
          </div>

          {/* Ticket meta */}
          <table style={{ width: '100%', fontSize: 10, marginBottom: 4, borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ ...CELL_LBL, width: '25%' }}>Ticket No.</td>
                <td style={{ ...CELL_BASE, fontFamily: 'monospace' }}>{t.ticket_no || '—'}</td>
                <td style={{ ...CELL_LBL, width: '25%' }}>
                  {mode === 'dropoff' ? 'Received On' : 'Handover Date'}
                </td>
                <td style={CELL_BASE}>{mode === 'dropoff' ? receivedDate : today}</td>
              </tr>
              <tr>
                <td style={CELL_LBL}>Patient</td>
                <td style={CELL_BASE}>{patient?.name || t.patient_name || '—'}</td>
                <td style={CELL_LBL}>Mobile</td>
                <td style={CELL_BASE}>{patient?.mobile || t.patient_mobile || '—'}</td>
              </tr>
              <tr>
                <td style={CELL_LBL}>MRD</td>
                <td style={CELL_BASE}>{patient?.mrd || '—'}</td>
                <td style={CELL_LBL}>Service Kind</td>
                <td style={CELL_BASE}>{KIND_LABEL[t.kind] || t.kind || '—'}</td>
              </tr>
            </tbody>
          </table>

          {/* Hearing aid details */}
          <div style={SECTION}>Hearing Aid Details</div>
          <table style={{ width: '100%', fontSize: 10, marginBottom: 4, borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ ...CELL_LBL, width: '30%' }}>Make / Model</td>
                <td style={CELL_BASE} colSpan={3}>{brandLine()}</td>
              </tr>
              <tr>
                <td style={CELL_LBL}>Type</td>
                <td style={{ ...CELL_BASE, width: '20%' }}>{t.instrument_type || '—'}</td>
                <td style={{ ...CELL_LBL, width: '20%' }}>Side</td>
                <td style={CELL_BASE}>{sideText()}</td>
              </tr>
              <tr>
                <td style={CELL_LBL}>Serial No.</td>
                <td style={{ ...CELL_BASE, fontFamily: 'monospace' }} colSpan={3}>
                  {t.instrument_serial || t.serial_no || '—'}
                </td>
              </tr>
              <tr>
                <td style={CELL_LBL}>Purchased From</td>
                <td style={CELL_BASE} colSpan={3}>{
                  t.purchased_from === 'our_clinic' ? 'Our Clinic'
                  : t.purchased_from === 'outside' ? `Outside — ${t.purchased_from_name || '—'}`
                  : '—'
                }</td>
              </tr>
              <tr>
                <td style={CELL_LBL}>Purchase Date</td>
                <td style={CELL_BASE}>{t.purchase_date || '—'}</td>
                <td style={CELL_LBL}>Warranty Card</td>
                <td style={CELL_BASE}>{warrantyCardText()}</td>
              </tr>
              <tr>
                <td style={CELL_LBL}>Warranty Status</td>
                <td style={CELL_BASE} colSpan={3} data-testid="service-slip-warranty-status">
                  {t.warranty_status === 'under_warranty'
                    ? <b style={{ color: '#047857' }}>Under Warranty</b>
                    : t.warranty_status === 'out_of_warranty'
                      ? <b style={{ color: '#be123c' }}>Out of Warranty</b>
                      : (t.warranty_covered ? <b style={{ color: '#047857' }}>Under Warranty</b> : '—')}
                </td>
              </tr>
              <tr>
                <td style={CELL_LBL}>Accessories</td>
                <td style={CELL_BASE} colSpan={3}>{accessoriesText()}</td>
              </tr>
            </tbody>
          </table>

          {/* Complaint */}
          <div style={SECTION}>Complaint / Reason for Service</div>
          <div style={{
            border: '0.5px solid #cbd5e1', padding: '4px 6px', fontSize: 10,
            minHeight: 28, whiteSpace: 'pre-wrap',
          }}>{t.complaint || '—'}</div>

          {mode === 'dropoff' ? (
            <>
              {/* Sep 2026 · Under-warranty callout printed above the T&C on
                  the drop-off slip so the patient sees the zero-charge
                  commitment before signing. Hidden for out-of-warranty
                  and legacy tickets. */}
              {(t.warranty_status === 'under_warranty' || (t.warranty_covered && !t.warranty_status)) && (
                <div style={{
                  marginTop: 6, border: '0.8px solid #047857', background: '#ecfdf5',
                  padding: '5px 8px', fontSize: 10, color: '#065f46',
                }} data-testid="service-slip-under-warranty-notice">
                  <b>Under Warranty — No service charge applicable.</b> Courier charges if any will be communicated separately.
                </div>
              )}
              {t.rough_estimate && (
                <>
                  <div style={SECTION}>Rough Estimate (indicative only)</div>
                  <div style={{
                    border: '0.5px solid #cbd5e1', padding: '4px 6px', fontSize: 10,
                  }}>{t.rough_estimate}</div>
                </>
              )}
              <div style={SECTION}>Terms &amp; Conditions</div>
              <ol style={{ fontSize: 9, margin: 0, paddingLeft: 14, lineHeight: 1.35 }}>
                <li>The clinic is not responsible for any accessories, batteries or moulds not explicitly listed above.</li>
                <li>The rough estimate is indicative. Final quotation will be confirmed after inspection / vendor evaluation.</li>
                <li>The patient will be contacted for approval before any chargeable repair is undertaken.</li>
                <li>Estimated turnaround is 10 – 14 working days for vendor repairs, subject to spare-parts availability.</li>
                <li>Please carry this acknowledgement slip at the time of pick-up.</li>
              </ol>
            </>
          ) : (
            <>
              <div style={SECTION}>Work Performed / Service Done</div>
              <div style={{
                border: '0.5px solid #cbd5e1', padding: '4px 6px', fontSize: 10,
                minHeight: 32, whiteSpace: 'pre-wrap',
              }}>
                {[
                  t.inspection_notes && `Inspection: ${t.inspection_notes}`,
                  // Only the APPROVED estimate's repair notes are printed as
                  // work-performed narrative — an unapproved / rejected quote
                  // must never imply work that was never done. `latestEstimate`
                  // is kept above for other paths but deliberately unused here.
                  approvedEstimate?.repair_notes && `Repair notes: ${approvedEstimate.repair_notes}`,
                  t.resolution_notes && `Resolution: ${t.resolution_notes}`,
                  t.handover_notes && `Handover: ${t.handover_notes}`,
                ].filter(Boolean).join('\n') || 'Standard service and quality check performed. Refer to the ticket record for detailed notes.'}
              </div>

              {/* Amount block */}
              <div style={{
                marginTop: 6, border: '0.8px solid #0f172a', padding: '5px 8px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: '#f1f5f9',
              }} data-testid="service-slip-final-amount">
                <div style={{ fontSize: 10 }}>
                  <div style={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Final Amount Charged (incl. GST)
                  </div>
                  <div style={{ fontSize: 8, color: '#475569', marginTop: 1 }}>
                    Final amount = cost + GST (as conveyed to patient at approval).
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, fontFamily: 'monospace' }}>
                  {t.warranty_covered
                    ? '₹0 (WARRANTY)'
                    : (finalAmount != null && finalAmount > 0)
                      ? fmtINR(finalAmount)
                      : (t.cost_to_patient && Number(t.cost_to_patient) > 0)
                        ? fmtINR(t.cost_to_patient)
                        : '— (see invoice)'}
                </div>
              </div>
              {t.invoice_no && (
                <div style={{ fontSize: 9, marginTop: 3, color: '#475569' }}>
                  Linked GST Invoice: <b style={{ fontFamily: 'monospace', color: '#0f172a' }}>{t.invoice_no}</b>
                </div>
              )}

              <div style={SECTION}>Handover Notes</div>
              <ol style={{ fontSize: 9, margin: 0, paddingLeft: 14, lineHeight: 1.35 }}>
                <li>The hearing aid has been checked and handed over in working condition on the date above.</li>
                <li>Please carry this slip for any warranty-related claim within the manufacturer's warranty window.</li>
                <li>Any post-handover complaint must be reported to the clinic within 7 days for a free re-check.</li>
                <li>Standard usage &amp; care guidelines apply. Please consult the audiologist for maintenance schedule.</li>
              </ol>
            </>
          )}

          {/* Signature block */}
          <div style={{
            marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 12,
          }}>
            <div style={{
              flex: 1, borderTop: '0.5px solid #0f172a', paddingTop: 3,
              fontSize: 9, textAlign: 'center',
            }}>
              <div style={{ fontWeight: 700 }}>
                {mode === 'dropoff' ? 'Received by (Clinic)' : 'Handed over by (Clinic)'}
              </div>
              <div style={{ color: '#64748b', marginTop: 1 }}>Name &amp; Signature</div>
            </div>
            <div style={{
              flex: 1, borderTop: '0.5px solid #0f172a', paddingTop: 3,
              fontSize: 9, textAlign: 'center',
            }}>
              <div style={{ fontWeight: 700 }}>
                {mode === 'dropoff' ? 'Patient / Authorised Signatory' : 'Received by (Patient)'}
              </div>
              <div style={{ color: '#64748b', marginTop: 1 }}>Signature &amp; Date</div>
            </div>
          </div>

          <div style={{
            marginTop: 5, textAlign: 'center', fontSize: 8,
            color: '#94a3b8', fontStyle: 'italic',
          }}>
            Generated on {today} · {t.ticket_no || ''}
          </div>
        </div>
      </div>
    </div>
  );
}
